const guide = `# Documentation du Portail Fournisseur

## Aperçu

Le Portail Fournisseur permet aux fournisseurs de demander des eSIM, de gérer l'inventaire, d'assigner des eSIM aux clients et de suivre l'état d'activation. Ce guide décrit le flux de travail complet, les API disponibles, les exigences de l'interface et le comportement du système.

**Chemin de base de l'API :** \`https://r4lsolutions.com/backend/api/provider\`

> Tous les endpoints nécessitent un jeton d'authentification fournisseur valide.

---

# Correspondance des statuts eSIM

Le backend renvoie des valeurs de statut internes qui doivent être affichées avec des libellés conviviaux.

| Statut backend           | Libellé affiché        |
| ------------------------ | ---------------------- |
| \`waiting_for_assignment\` | En stock               |
| \`pre_service\`            | En attente d'activation |
| \`in_service\`             | Actif                  |
| \`terminated\`             | Résilié                |

---

# Correspondance des statuts de demande

| Statut     | Signification                                 |
| ---------- | --------------------------------------------- |
| \`pending\`  | Demande soumise et en attente d'approbation   |
| \`approved\` | Demande approuvée et eSIM ajoutées à l'inventaire |
| \`rejected\` | Demande refusée                               |

---

# Flux de travail du fournisseur

\`\`\`text
Demander des SIM
      ↓
Approbation de la demande
      ↓
eSIM ajoutée à l'inventaire
      ↓
Assigner l'eSIM au client
      ↓
Générer le code QR
      ↓
Le client active l'eSIM
      ↓
Synchroniser le statut
      ↓
L'eSIM devient active
\`\`\`

---

# 1. Demander des eSIM

Les fournisseurs peuvent soumettre des demandes de nouvel inventaire eSIM.

### API

\`\`\`http
POST https://r4lsolutions.com/backend/api/provider/sim-requests
\`\`\`

### Corps de la requête

\`\`\`json
{
  "quantity": 5,
  "note": "Need for Q3 campaign"
}
\`\`\`

### Validation

| Champ    | Type   | Requis | Notes               |
| -------- | ------ | -------- | ------------------- |
| quantity | Nombre | Oui      | Plage : 1–500       |
| note     | Texte  | Non      | Commentaires supplémentaires |

### Comportement attendu

* Créer une nouvelle demande.
* Le statut de la demande est automatiquement défini sur \`pending\`.
* La demande nouvellement créée doit apparaître dans la liste des demandes.

---

# 2. Voir mes demandes de SIM

Les fournisseurs peuvent suivre les demandes soumises et surveiller le statut d'approbation.

### API

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/sim-requests
\`\`\`

### Exemple

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/sim-requests?page=1&pageSize=20&status=pending
\`\`\`

### Filtres disponibles

| Filtre    | Description                 |
| --------- | --------------------------- |
| status    | pending, approved, rejected |
| search    | Rechercher par ID de demande |
| startDate | Filtrer par date de création |
| endDate   | Filtrer par date de création |

### API des statistiques

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/sim-requests/stats
\`\`\`

### Exigences de l'interface

Afficher :

* ID de demande
* Quantité
* Notes
* Date de création
* Badge de statut

### Badges de statut

* En attente
* Approuvé
* Rejeté

> Une fois une demande approuvée, les eSIM associées deviennent automatiquement disponibles dans la section Inventaire.

---

# 3. Gestion de l'inventaire eSIM

L'inventaire affiche toutes les eSIM assignées au fournisseur.

### API

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/esims
\`\`\`

### Exemple

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/esims?assigned=false&status=pre_service&page=1&pageSize=20
\`\`\`

### Filtres disponibles

| Filtre    | Description       |
| --------- | ----------------- |
| assigned  | true / false      |
| status    | Statut de l'eSIM  |
| search    | Recherche par ICCID |
| startDate | Filtre par plage de dates |
| endDate   | Filtre par plage de dates |

### API des statistiques

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/esims/stats
\`\`\`

### Champs de réponse

| Champ           | Description                  |
| --------------- | ---------------------------- |
| iccid           | Identifiant unique de l'eSIM |
| activation_code | Code d'activation (chaîne LPA) |
| status          | Statut actuel de l'eSIM      |
| customer_id     | Client assigné               |
| activated_at    | Horodatage d'activation      |

### Exigences de l'interface

Afficher :

* ICCID
* Statut
* Client assigné
* Date d'activation
* Actions

### Actions

| Action      | Disponibilité         |
| ----------- | --------------------- |
| Assign      | eSIM non assignées    |
| Sync Status | Tous les enregistrements d'inventaire |

### Vues de l'inventaire

**Disponibles pour assignation**

\`\`\`text
assigned = false
\`\`\`

Affiche les eSIM prêtes à être assignées aux clients.

---

# 4. Assigner une eSIM à un client

Cette étape lie une eSIM disponible à un client et génère les identifiants d'activation.

### API

\`\`\`http
PATCH https://r4lsolutions.com/backend/api/provider/esims/{iccid}/assign
\`\`\`

### Corps de la requête

\`\`\`json
{
  "customer_id": "cust_abc123"
}
\`\`\`

### Réponse

Renvoie :

* Informations sur l'eSIM assignée
* Code d'activation (chaîne LPA)

Exemple :

\`\`\`text
LPA:1$consumer.e-sim.global$TN2025...
\`\`\`

---

## Exigences frontend après assignation

### Générer le code QR

Le \`activation_code\` renvoyé doit être converti en code QR.

### Afficher le code d'activation

Fournir le code d'activation brut comme option de secours.

Exemple :

\`\`\`text
LPA:1$consumer.e-sim.global$TN2025...
\`\`\`

### Fonctionnalité de copie

Permettre au fournisseur de copier le code d'activation.

### Important

Assigner une eSIM **ne l'active pas**.

Le statut reste :

\`\`\`text
En attente d'activation
\`\`\`

jusqu'à ce que le client installe le profil eSIM.

---

# 5. Activation client

Aucune action du fournisseur n'est requise pendant l'activation.

### Processus

1. Le fournisseur partage le code QR.
2. Le client scanne le code QR sur son appareil.
3. L'opérateur installe le profil eSIM.
4. Le backend reçoit la confirmation d'activation.
5. Le statut change automatiquement :

\`\`\`text
pre_service → in_service
\`\`\`

### Exigence frontend

Aucun appel API n'est nécessaire.

Le fournisseur doit uniquement actualiser ou synchroniser le statut de l'inventaire.

---

# 6. Synchroniser le statut d'activation

Permet aux fournisseurs de récupérer les dernières mises à jour d'activation auprès de l'opérateur.

### API

\`\`\`http
POST https://r4lsolutions.com/backend/api/provider/esims/sync
\`\`\`

### Exemple de réponse

\`\`\`json
{
  "total_checked": 5,
  "total_updated": 2,
  "updated": [
    {
      "iccid": "8910300000041170580",
      "old_status": "pre_service",
      "new_status": "in_service",
      "activated_at": "2026-06-17T08:00:00.000Z"
    }
  ],
  "failed": []
}
\`\`\`

### Comportement frontend

Après une synchronisation réussie :

1. Recharger les données d'inventaire.
2. Mettre à jour les badges de statut.
3. Afficher les horodatages d'activation.
4. Déplacer les eSIM activées vers l'état Actif.

### Transition de statut

\`\`\`text
En attente d'activation
          ↓
       Actif
\`\`\`

---

# Gestion des clients

Les fournisseurs peuvent gérer les clients via le module clients.

### API liste des clients

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/customers
\`\`\`

### API statistiques clients

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/customers/stats
\`\`\`

---

# Référence API complète

## Demandes de SIM

| Méthode | Endpoint                           | Description            |
| ------ | ---------------------------------- | ---------------------- |
| POST   | \`https://r4lsolutions.com/backend/api/provider/sim-requests\`       | Créer une nouvelle demande de SIM |
| GET    | \`https://r4lsolutions.com/backend/api/provider/sim-requests\`       | Lister les demandes de SIM |
| GET    | \`https://r4lsolutions.com/backend/api/provider/sim-requests/stats\` | Statistiques des demandes |

---

## Inventaire eSIM

| Méthode | Endpoint                             | Description             |
| ------ | ------------------------------------ | ----------------------- |
| GET    | \`https://r4lsolutions.com/backend/api/provider/esims\`                | Lister l'inventaire eSIM |
| GET    | \`https://r4lsolutions.com/backend/api/provider/esims/stats\`          | Statistiques d'inventaire |
| PATCH  | \`https://r4lsolutions.com/backend/api/provider/esims/{iccid}/assign\` | Assigner une eSIM à un client |
| POST   | \`https://r4lsolutions.com/backend/api/provider/esims/sync\`           | Synchroniser le statut d'activation |

---

## Clients

| Méthode | Endpoint                        | Description         |
| ------ | ------------------------------- | ------------------- |
| GET    | \`https://r4lsolutions.com/backend/api/provider/customers\`       | Liste des clients   |
| GET    | \`https://r4lsolutions.com/backend/api/provider/customers/stats\` | Statistiques clients |

---

# Cycle de vie de bout en bout

\`\`\`text
Le fournisseur demande des eSIM
          ↓
Demande approuvée
          ↓
eSIM ajoutée à l'inventaire
          ↓
Le fournisseur assigne l'eSIM
          ↓
Code QR généré
          ↓
Le client installe l'eSIM
          ↓
L'opérateur confirme l'activation
          ↓
Le fournisseur synchronise le statut
          ↓
L'eSIM devient active
\`\`\`

Ce flux de travail permet aux fournisseurs de gérer efficacement l'inventaire, de distribuer des eSIM aux clients et de suivre la progression de l'activation depuis un seul portail.
`;

export default guide;
